/* eslint-disable */
import { Component, OnInit, ViewChild, AfterViewInit, OnChanges, OnDestroy, HostBinding, HostListener, Input, ElementRef, Renderer2, EventEmitter, Output, ContentChild, TemplateRef, ChangeDetectorRef, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Util } from '@libs/utilities/util';

import { NgSliderElementDirective } from './directives/slider-element.directive';
import { NgSliderHandleDirective } from './directives/slider-handle.directive';
import { NgSliderLabelDirective } from './directives/slider-label.directive';

import { NgEventListener } from './event-listener/event-listener.data';
import { NgEventListenerHelper } from './event-listener/event-listener-helper';
import { CustomizableSliderHelper } from './shared/customizable-slider.helper';
import { NgInputModelChange, NgOutputModelChange, NgDragging, NgTick, NgChangeContext, NgCustomizableSliderOptions } from './shared/customizable-slider.data';

import { NgSliderEventsExtension } from './extensions/slider-events.extension';
import { NgSliderHandlerExtension } from './extensions/slider-handler.extension';
import { NgSliderValueUpdateExtension } from './extensions/slider-value-update.extension';
import { NgSliderSubscriptionExtension } from './extensions/slider-subscriptions.extension';
import { NgSliderHelperExtension } from './extensions/slider-helper.extension';
import { EnumNgPointerType, INgSliderBaseStyle } from './shared/customizable-slider.interface';
import { NgSliderTickHelperExtension } from './extensions/slider-tick-helper.extension';


@Component({
    selector: 'ng-customizable-slider',
    templateUrl: './customizable-slider.component.html',
    styleUrls: ['./customizable-slider.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NgCustomizableSliderComponent),
            multi: true,
        },
        // WORKAROUND: Extensions need to be injected and not in the module,
        // > because state should be component based in order to load multiple
        // > slider on same page
        NgSliderHelperExtension,
        NgSliderValueUpdateExtension,
        NgSliderHandlerExtension,
        NgSliderEventsExtension,
        NgSliderSubscriptionExtension,
        NgSliderTickHelperExtension
    ],
    standalone: false
})
export class NgCustomizableSliderComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

	@Input() value				: number 						= null!;
	@Input() highValue			: number 						= null!;
	@Input() options			: NgCustomizableSliderOptions 	= new NgCustomizableSliderOptions();

	@Output() valueChange		: EventEmitter<number> 			= new EventEmitter();
	@Output() highValueChange	: EventEmitter<number>	 		= new EventEmitter();
	@Output() userChangeStart	: EventEmitter<NgChangeContext> = new EventEmitter();
	@Output() userChange		: EventEmitter<NgChangeContext> = new EventEmitter();
	@Output() userChangeEnd		: EventEmitter<NgChangeContext> = new EventEmitter();

	//* Slider DOM elements */
	@ViewChild('fullBar', {read: NgSliderElementDirective}) 				fullBarElement					!: NgSliderElementDirective;
	@ViewChild('ceilLabel', {read: NgSliderLabelDirective}) 				ceilLabelElement				!: NgSliderLabelDirective;
	@ViewChild('minHandle', {read: NgSliderHandleDirective}) 				minHandleElement				!: NgSliderHandleDirective;
	@ViewChild('maxHandle', {read: NgSliderHandleDirective}) 				maxHandleElement				!: NgSliderHandleDirective;
	@ViewChild('floorLabel', {read: NgSliderLabelDirective}) 				floorLabelElement				!: NgSliderLabelDirective;
	@ViewChild('combinedLabel', {read: NgSliderLabelDirective}) 			combinedLabelElement			!: NgSliderLabelDirective;
	@ViewChild('ticksElement', {read: NgSliderElementDirective}) 			ticksElement					!: NgSliderElementDirective;
	@ViewChild('selectionBar', {read: NgSliderElementDirective}) 			selectionBarElement				!: NgSliderElementDirective;
	@ViewChild('minHandleLabel', {read: NgSliderLabelDirective}) 			minHandleLabelElement			!: NgSliderLabelDirective;
	@ViewChild('maxHandleLabel', {read: NgSliderLabelDirective}) 			maxHandleLabelElement			!: NgSliderLabelDirective;
	@ViewChild('leftOuterSelectionBar', {read: NgSliderElementDirective}) 	leftOuterSelectionBarElement	!: NgSliderElementDirective;
	@ViewChild('rightOuterSelectionBar', {read: NgSliderElementDirective}) 	rightOuterSelectionBarElement	!: NgSliderElementDirective;
	@ContentChild('tooltipTemplate') 										tooltipTemplate					!: TemplateRef<any>;

	// Host element class bindings
	@HostBinding('class.vertical') 		sliderElementVerticalClass		: boolean 	= false;
	@HostBinding('class.animate') 		sliderElementAnimateClass		: boolean 	= false;
	@HostBinding('class.with-legend') 	sliderElementWithLegendClass	: boolean 	= false;
	@HostBinding('attr.disabled') 		sliderElementDisabledAttr		: string 	= null!;
	@HostBinding('attr.aria-label') 	sliderElementAriaLabel			: string 	= 'customizable-slider';

	//** Helper Variables */
	initHasRun						: boolean 					= false;
	inputModelChangeSubject			: Subject<NgInputModelChange> = new Subject<NgInputModelChange>();
	outputModelChangeSubject		: Subject<NgOutputModelChange>= new Subject<NgOutputModelChange>();
	inputModelChangeSubscription	: Subscription 				= null!;
	outputModelChangeSubscription	: Subscription 				= null!;

	viewLowValue					: number 					= null!;
	viewHighValue					: number 					= null!;
	viewOptions						: NgCustomizableSliderOptions = new NgCustomizableSliderOptions();

	handleHalfDimension				: number 					= 0;
	maxHandlePosition				: number 					= 0;
	touchId							: number 					= null!;
	dragging						: NgDragging 				= new NgDragging();
	firstKeyDown					: boolean			 		= false;
	currentFocusPointer				: EnumNgPointerType 		= null!;
	currentTrackingPointer			: EnumNgPointerType 		= null!;

	//** CSS styles & class flags helpers */
	ticks							: NgTick[] 					= [];
	minPointerStyle					: object 					= {};
	maxPointerStyle					: object 					= {};
	barStyle						: INgSliderBaseStyle 		= {};
	ticksUnderValuesClass			: boolean 					= false;
	fullBarTransparentClass			: boolean 					= false;
	selectionBarDraggableClass		: boolean 					= false;

	//** Event listeners */
	onMoveEventListener				: NgEventListener 			= null!;
	onEndEventListener				: NgEventListener 			= null!;
	eventListenerHelper				: NgEventListenerHelper 	= null!;
	moving							: boolean 					= false;
	resizeObserver					: ResizeObserver 			= null!;
	intermediateTicks				: boolean 					= false;
	onTouchedCallback				: (value: any) => void 		= null!;
	onChangeCallback				: (value: any) => void 		= null!;

	//** Getter Helpers */
	get range()		: boolean { return (Util.Basic.isDefined(this.value) && Util.Basic.isDefined(this.highValue)); }
	get showTicks()	: boolean { return this.viewOptions.showTicks!; }


	/**------------------------------------------------------
	 * Handle Subscriptions
	 */
	manualRefreshSubscription!: Subscription;
	triggerFocusSubscription !: Subscription;

	@Input()
	set manualRefresh(manualRefresh: EventEmitter<void>) {
		this.sliderSubscriptionExtension.unsubscribeManualRefresh();
		this.manualRefreshSubscription = manualRefresh.subscribe(() => { setTimeout(() => this.sliderValueUpdateExtension.calculateViewDimensionsAndDetectChanges())});
	}
	@Input()
	set triggerFocus(triggerFocus: EventEmitter<void>) {
		this.sliderSubscriptionExtension.unsubscribeTriggerFocus();
		this.triggerFocusSubscription = triggerFocus.subscribe((pointerType: EnumNgPointerType) => this.sliderValueUpdateExtension.focusPointer(pointerType));
	}

	constructor(
		private renderer					: Renderer2,
		public  elementRef					: ElementRef,
		public  changeDetectionRef			: ChangeDetectorRef,
		public  customizableSliderHelper	: CustomizableSliderHelper,

		public  sliderEventsExtension		: NgSliderEventsExtension,
		public  sliderHandlerExtension		: NgSliderHandlerExtension,
		public  sliderHelperExtension		: NgSliderHelperExtension,
		public  sliderSubscriptionExtension	: NgSliderSubscriptionExtension,
		public  sliderValueUpdateExtension	: NgSliderValueUpdateExtension,
		public  sliderTickHelperExtension	: NgSliderTickHelperExtension
	) {

		//0 - Set event listener
		this.eventListenerHelper = new NgEventListenerHelper(this.renderer);

		//1 - Initialize the extension
		this.sliderEventsExtension.initialize(this);
		this.sliderHandlerExtension.initialize(this);
		this.sliderHelperExtension.initialize(this);
		this.sliderSubscriptionExtension.initialize(this);
		this.sliderTickHelperExtension.initialize(this);
		this.sliderValueUpdateExtension.initialize(this);
	}


	/**------------------------------------------------------
	 * Lifecycle Hooks
	 */
	ngOnInit() {

		//0 - assign view options
		this.viewOptions = new NgCustomizableSliderOptions();
		Object.assign(this.viewOptions, this.options);

		//1 - update state and label
		this.sliderHandlerExtension.updateDisabledState();
		this.sliderHandlerExtension.updateVerticalState();
		this.sliderHandlerExtension.updateAriaLabel();
	}

	ngAfterViewInit() {

		//0 - apply options and subscribe input change
		this.sliderValueUpdateExtension.applyOptions();
		this.sliderSubscriptionExtension.subscribeInputModelChangeSubject();
		this.sliderSubscriptionExtension.subscribeOutputModelChangeSubject();
		this.sliderValueUpdateExtension.reNormalizedModelValues();

		//1 - set view low and high value
		this.viewLowValue 	= this.sliderHelperExtension.modelValueToViewValue(this.value);
		this.viewHighValue 	= (this.range)
			? this.sliderHelperExtension.modelValueToViewValue(this.highValue)
			: this.viewHighValue = null!;

		//2 - update states
		this.sliderHandlerExtension.updateVerticalState(); // need to run this again to cover changes to slider elements
		this.sliderHandlerExtension.manageElementsStyle();
		this.sliderHandlerExtension.updateDisabledState();
		this.sliderValueUpdateExtension.calculateViewDimensions();
		this.sliderValueUpdateExtension.addAccessibility();
		this.sliderValueUpdateExtension.updateCeilLabel();
		this.sliderValueUpdateExtension.updateFloorLabel();
		this.sliderHandlerExtension.initHandles();
		this.sliderHandlerExtension.manageEventsBindings();
		this.sliderHandlerExtension.updateAriaLabel();
		this.sliderSubscriptionExtension.subscribeResizeObserver();
		this.initHasRun = true;

		//3 - run change detection manually to resolve some issues when init procedure changes values used in the view
		if (!this.sliderValueUpdateExtension.isRefDestroyed()) this.changeDetectionRef.detectChanges();
	}

	ngOnChanges(changes: SimpleChanges) {

		//0 - always apply options first
		if (Util.Basic.isDefined(changes?.['options']) && Util.Basic.stringifyObject(changes?.['options']?.previousValue) !== Util.Basic.stringifyObject(changes?.['options']?.currentValue)) this.onChangeOptions();

		//1 - then value changes
		if (Util.Basic.isDefined(changes?.['value']) || Util.Basic.isDefined(changes?.['highValue'])) {
			this.inputModelChangeSubject.next({
				value			: this.value,
				highValue		: this.highValue,
				forceChange		: false,
				internalChange	: false
			});
		}
	}

	ngOnDestroy() {
		this.unbindEvents();
		this.sliderSubscriptionExtension.unsubscribeOnDestroy();
	}


	/**------------------------------------------------------
	 * Control Value Accessor
	 */
	writeValue(obj: any) {

		//0 - check object type
		if (obj instanceof Array) {
			this.value 		= obj[0];
			this.highValue 	= obj[1];
		} else {
			this.value 		= obj;
		}

		//1 - ngOnChanges() is not called in this instance, so we need to communicate the change manually
		this.inputModelChangeSubject.next({
			value			: this.value,
			highValue		: this.highValue,
			forceChange		: false,
			internalChange	: false
		});
	}

	registerOnChange(onChangeCallback: any) {
		this.onChangeCallback = onChangeCallback;
	}

	registerOnTouched(onTouchedCallback: any) {
		this.onTouchedCallback = onTouchedCallback;
	}

	setDisabledState(isDisabled: boolean) {
		this.viewOptions.disabled = isDisabled;
		this.sliderHandlerExtension.updateDisabledState();
	}

	setAriaLabel(ariaLabel: string) {
		this.viewOptions.ariaLabel = ariaLabel;
		this.sliderHandlerExtension.updateAriaLabel();
	}


	//** Detect on window resize */
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.sliderValueUpdateExtension.calculateViewDimensionsAndDetectChanges();
	}

	//** Get cursor pointer position */
	getPointerElement(pointerType: EnumNgPointerType): NgSliderHandleDirective {
		if (pointerType === EnumNgPointerType.Min) return this.minHandleElement;
		if (pointerType === EnumNgPointerType.Max) return this.maxHandleElement;
		return null!;
	}

	//** Bind events */
	bindEvents() {

		//0 - set draggable range
		const draggableRange: boolean = this.viewOptions.draggableRange!;

		//1 - bind mouse down event
		if (!this.viewOptions.onlyBindHandles) this.selectionBarElement.on('mousedown', (event: MouseEvent) => this.sliderHelperExtension.onBarStart(null!, draggableRange, event, true, true, true));
		if (this.viewOptions.draggableRangeOnly) {
			this.minHandleElement.on('mousedown', (event: MouseEvent) => this.sliderHelperExtension.onBarStart(EnumNgPointerType.Min, draggableRange, event, true, true));
			this.maxHandleElement.on('mousedown', (event: MouseEvent) => this.sliderHelperExtension.onBarStart(EnumNgPointerType.Max, draggableRange, event, true, true));
		} else {
			this.minHandleElement.on('mousedown',(event: MouseEvent) => this.sliderEventsExtension.onStart(EnumNgPointerType.Min, event, true, true));
			if (this.range) this.maxHandleElement.on('mousedown', (event: MouseEvent) => this.sliderEventsExtension.onStart(EnumNgPointerType.Max, event, true, true));
			if (!this.viewOptions.onlyBindHandles) {
				this.fullBarElement.on('mousedown', (event: MouseEvent) => this.sliderEventsExtension.onStart(null!, event, true, true, true));
				this.ticksElement.on('mousedown', (event: MouseEvent) => this.sliderEventsExtension.onStart(null!, event, true, true, true, true));
			}
		}

		//2 - bind touch start on passive event
		if (!this.viewOptions.onlyBindHandles) this.selectionBarElement.onPassive('touchstart', (event: TouchEvent) => this.sliderHelperExtension.onBarStart(null!, draggableRange, event, true, true, true));
		if (this.viewOptions.draggableRangeOnly) {
			this.minHandleElement.onPassive('touchstart', (event: TouchEvent) => this.sliderHelperExtension.onBarStart(EnumNgPointerType.Min, draggableRange, event, true, true));
			this.maxHandleElement.onPassive('touchstart', (event: TouchEvent) => this.sliderHelperExtension.onBarStart(EnumNgPointerType.Max, draggableRange, event, true, true));
		} else {
			this.minHandleElement.onPassive('touchstart', (event: TouchEvent) => this.sliderEventsExtension.onStart(EnumNgPointerType.Min, event, true, true));
			if (this.range) this.maxHandleElement.onPassive('touchstart',(event: TouchEvent) => this.sliderEventsExtension.onStart(EnumNgPointerType.Max, event, true, true));
			if (!this.viewOptions.onlyBindHandles) {
				this.fullBarElement.onPassive('touchstart',(event: TouchEvent) => this.sliderEventsExtension.onStart(null!, event, true, true, true));
				this.ticksElement.onPassive('touchstart', (event: TouchEvent) => this.sliderEventsExtension.onStart(null!, event, false, false, true, true));
			}
		}

		//3 - if keyboard support then bind focus event
		if (this.viewOptions.keyboardSupport) {
			this.minHandleElement.on('focus', () => this.sliderEventsExtension.onPointerFocus(EnumNgPointerType.Min));
			if (this.range) this.maxHandleElement.on('focus', () => this.sliderEventsExtension.onPointerFocus(EnumNgPointerType.Max));
		}
	}

	private onChangeOptions() {

		//0 - check if init not started yet
		if (!this.initHasRun) return;

		//1 - get and apply influencing event binding
		const previousOptionsInfluencingEventBindings: boolean[] = this.getOptionsInfluencingEventBindings(this.viewOptions);
		this.sliderValueUpdateExtension.applyOptions();

		//2 - get and check rebind influencing even bindings
		const newOptionsInfluencingEventBindings: boolean[] = this.getOptionsInfluencingEventBindings(this.viewOptions);
		const rebindEvents: boolean = !Util.Array.isEqual(previousOptionsInfluencingEventBindings, newOptionsInfluencingEventBindings);
		this.sliderValueUpdateExtension.reNormalizedModelValues();

		//3 - set view low value
		this.viewLowValue = this.sliderHelperExtension.modelValueToViewValue(this.value);

		//4 - set high value
		this.viewHighValue = (this.range)
			? this.sliderHelperExtension.modelValueToViewValue(this.highValue)
			: this.viewHighValue = null!;

		//5 - reset range slider
		this.sliderValueUpdateExtension.resetSlider(rebindEvents);
	}

	//** Get influencing event bindings */
	private getOptionsInfluencingEventBindings(options: NgCustomizableSliderOptions): boolean[] {
		return [
			options.disabled!,
			options.readOnly!,
			options.draggableRange!,
			options.draggableRangeOnly!,
			options.onlyBindHandles!,
			options.keyboardSupport!
		];
	}

	//** Unbind events */
	unbindEvents() {

		//0 - unsubscribe mouse event
		this.sliderSubscriptionExtension.unsubscribeOnMove();
		this.sliderSubscriptionExtension.unsubscribeOnEnd();

		//1 - unbind slider events
		for (const element of this.sliderHandlerExtension.getAllSliderElements()) if (Util.Basic.isDefined(element)) element.off();
	}


	/**------------------------------------------------------
	 * Host Bindings
	 */
	@HostBinding('class')
	get class() {
		return 'customizable-slider';
	}
}
